import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { CANNONS, cannonMount, type CannonSpec } from '../data/ship';
import { AMMO, type AmmoId, type ShipClass } from '../data/classes';
import type { SimEvents } from './events';
import type { Ship } from './ship';
import type { Ballistics } from './ballistics';
import type { GunCrewSlot } from './crew';

// The great guns of one ship. Guns only reload while men serve them; a
// reload takes around twenty seconds for a full, practised crew and far
// longer short-handed. Each gun fires along its own axis, so the ship's roll
// at the moment of firing throws the shot high or low - timing the roll is
// the gun captain's art.

const RELOAD = 22;

export interface Gun extends GunCrewSlot {
  /** Index into CANNONS. */
  index: number;
  hp: number;
  loaded: boolean;
  /** Reload progress 0..1. */
  reload: number;
  /** What's rammed home. */
  charge: AmmoId;
  recoil: number;
  sinceFired: number;
  /** Pending shot time (sim seconds) or -1. */
  fireAt: number;
  /** Quoin setting above the deck plane, rad. */
  elevation: number;
  /** Trained forward (+) or aft of the beam with handspikes, rad. */
  train: number;
}

/** How far a carriage gun can be trained off the beam. */
export const TRAIN_LIMIT = 0.26;

export class Battery {
  readonly guns: Gun[];
  /** Ammunition the crews load next. */
  ammo: AmmoId = 'round';
  readonly weight: number;
  private time = 0;
  /** Crew skill 0..1 (from the crew each step). */
  gunnery = 0.6;

  constructor(readonly cls: ShipClass, private readonly rng: Rng) {
    this.weight = cls.gunWeight;
    this.guns = cls.guns.map((index) => ({
      index, spec: CANNONS[index]!, destroyed: false, crew: 0, hp: 30 * Math.cbrt(cls.gunWeight),
      loaded: true, reload: 1, charge: 'round' as AmmoId, recoil: 0, sinceFired: 99, fireAt: -1, elevation: 0.02, train: 0,
    }));
  }

  /** Guns on a side loaded and able to fire. */
  ready(side: 1 | -1): number {
    let n = 0;
    for (const g of this.guns) if (g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0) n++;
    return n;
  }

  count(side: 1 | -1): number {
    let n = 0;
    for (const g of this.guns) if (g.spec.side === side && !g.destroyed) n++;
    return n;
  }

  /** Mean reload progress of a side's serviceable guns. */
  reloadProgress(side: 1 | -1): number {
    let p = 0, n = 0;
    for (const g of this.guns) {
      if (g.spec.side !== side || g.destroyed) continue;
      p += g.loaded ? 1 : g.reload;
      n++;
    }
    return n > 0 ? p / n : 0;
  }

  setElevation(side: 1 | -1, elevation: number): void {
    for (const g of this.guns) if (g.spec.side === side) g.elevation = clamp(elevation, -0.08, 0.14);
  }

  /** Train a side's guns forward (+) or aft (-) of the beam. */
  setTrain(side: 1 | -1, angle: number): void {
    for (const g of this.guns) if (g.spec.side === side) g.train = clamp(angle, -TRAIN_LIMIT, TRAIN_LIMIT);
  }

  /** Fire a ripple broadside, bow to stern. Returns guns fired. */
  fire(side: 1 | -1): number {
    const guns = this.guns
      .filter((g) => g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0 && g.crew > 0.2)
      .sort((a, b) => b.spec.x - a.spec.x);
    let t = this.time + 0.05;
    for (const g of guns) {
      g.fireAt = t;
      t += 0.16 + this.rng.range(0, 0.12);
    }
    return guns.length;
  }

  /** Fire one gun as it bears. */
  fireGun(i: number): boolean {
    const g = this.guns[i];
    if (!g || !g.loaded || g.destroyed || g.fireAt >= 0 || g.crew <= 0.2) return false;
    g.fireAt = this.time + this.rng.range(0, 0.25);
    return true;
  }

  damageGun(i: number, amount: number): boolean {
    const g = this.guns[i];
    if (!g || g.destroyed) return false;
    g.hp -= amount;
    if (g.hp <= 0) {
      g.destroyed = true;
      g.loaded = false;
      return true;
    }
    return false;
  }

  update(dt: number, ship: Ship, ballistics: Ballistics, events: SimEvents, vessel: number): void {
    this.time += dt;
    for (let i = 0; i < this.guns.length; i++) {
      const g = this.guns[i]!;
      g.sinceFired += dt;
      if (g.fireAt >= 0 && this.time >= g.fireAt) {
        g.fireAt = -1;
        if (g.loaded && !g.destroyed && g.crew > 0.2) this.shoot(i, g, ship, ballistics, events, vessel);
      }
      // Sponge, load, ram and run out: only with hands on the tackles.
      if (!g.loaded && !g.destroyed && g.sinceFired > 1.5) {
        const need = g.spec.deck === 'main' ? 3 : 2;
        const hands = clamp(g.crew / need, 0, 1.15);
        const size = g.spec.deck === 'main' ? 1 : 0.75;
        const t = RELOAD * size * AMMO[this.ammo].reload * Math.sqrt(this.weight) / (0.55 + 0.45 * this.gunnery);
        g.reload += (dt * Math.pow(hands, 0.8)) / t;
        if (g.reload >= 1) {
          g.loaded = true;
          g.reload = 1;
          g.charge = this.ammo;
        }
      }
      // Carriage recoil and running out again.
      const s = g.sinceFired;
      g.recoil = s < 0.12 ? s / 0.12 : g.loaded ? Math.max(0, g.recoil - dt / 3) : 1;
    }
  }

  private shoot(index: number, g: Gun, ship: Ship, ballistics: Ballistics, events: SimEvents, vessel: number): void {
    const ammo = AMMO[g.charge];
    const m = cannonMount(g.spec);
    const muzzle = ship.designToWorld(tmpA.set(m.x, m.y + 0.05, g.spec.side * (Math.abs(m.z) + g.spec.length * 0.6)), new Vec3());
    // Aim: the quoin elevation plus a shake that practised crews keep small.
    const spread = 0.004 + (1 - this.gunnery) * 0.009;
    const dir = tmpB.set(Math.tan(g.train) + this.rng.gauss() * spread, g.elevation + this.rng.gauss() * spread * 0.8, g.spec.side).normalize();
    ship.rot.rotate(dir);
    const mv = ammo.speed * (g.spec.deck === 'main' ? 1 : 0.88);
    for (let k = 0; k < ammo.count; k++) {
      const d = tmpC.copy(dir);
      if (ammo.spread > 0) {
        d.x += this.rng.gauss() * ammo.spread;
        d.y += this.rng.gauss() * ammo.spread * 0.7;
        d.z += this.rng.gauss() * ammo.spread;
        d.normalize();
      }
      const vel = new Vec3().copy(d).scale(mv * this.rng.range(0.97, 1.03)).add(ship.vel);
      ballistics.launch(vessel, muzzle, vel, g.charge, this.weight);
    }
    g.loaded = false;
    g.reload = 0;
    g.sinceFired = 0;
    events.push({ type: 'cannonFire', index, x: muzzle.x, y: muzzle.y, z: muzzle.z, dx: dir.x, dy: dir.y, dz: dir.z, vessel, weight: this.weight });
    // Shot momentum plus powder gases.
    const impulse = ammo.mass * ammo.count * this.weight * mv * 1.6 * (g.spec.deck === 'main' ? 1 : 0.5);
    ship.applyImpulse(-dir.x * impulse, -dir.y * impulse, -dir.z * impulse, muzzle.x, muzzle.y, muzzle.z);
  }
}

const tmpA = new Vec3();
const tmpB = new Vec3();
const tmpC = new Vec3();

export type { CannonSpec };
